import { apiClient } from './apiClient';

export type ProductResourceType =
  | 'Document'
  | 'Template'
  | 'Video'
  | 'Link'
  | 'Integration'
  | 'Other';

export type ProductResource = {
  id: string;
  name: string;
  description: string;
  resourceType: ProductResourceType;
  url?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type ProductResourceListResult = {
  items: ProductResource[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type ProductResourceUpsertPayload = {
  name: string;
  description?: string;
  resourceType?: ProductResourceType;
  url?: string;
  isActive?: boolean;
  sortOrder?: number;
};

export type SalesProductResourceLink = {
  productResourceId: string;
  name: string;
  resourceType: ProductResourceType;
  url?: string | null;
  sortOrder: number;
};

export type SalesProductResourceLinkItemPayload = {
  productResourceId: string;
  sortOrder: number;
};

export type ProductResourceListFilter = {
  activeOnly?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
};

export async function listProductResources(
  filter: ProductResourceListFilter = {},
): Promise<ProductResourceListResult> {
  const response = await apiClient.get<ProductResourceListResult>('/v1/sales/product-resources', {
    params: {
      activeOnly: filter.activeOnly ?? false,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? 25,
      search: filter.search || undefined,
    },
  });
  return response.data;
}

export async function createProductResource(
  payload: ProductResourceUpsertPayload,
): Promise<ProductResource> {
  const response = await apiClient.post<ProductResource>('/v1/sales/product-resources', payload);
  return response.data;
}

export async function updateProductResource(
  id: string,
  payload: ProductResourceUpsertPayload,
): Promise<ProductResource> {
  const response = await apiClient.put<ProductResource>(`/v1/sales/product-resources/${id}`, payload);
  return response.data;
}

export async function deleteProductResource(id: string): Promise<void> {
  await apiClient.delete(`/v1/sales/product-resources/${id}`);
}

export async function getProductResources(productId: string): Promise<SalesProductResourceLink[]> {
  const response = await apiClient.get<SalesProductResourceLink[]>(
    `/v1/sales/products/${productId}/product-resources`,
  );
  return response.data;
}

export async function replaceProductResources(
  productId: string,
  items: SalesProductResourceLinkItemPayload[],
): Promise<void> {
  await apiClient.put(`/v1/sales/products/${productId}/product-resources`, { items });
}
